/**
 * DecisionFlow — OteHistoryDataReview
 * data-fit LLC · Confidential
 * Stage 3: DATA SETUP — Screen SETUP-01b (OTE History)
 * Spec authority: UnifiedImportSpec_v1.md §3.6
 * MasterContext authority: MasterContext_S474.md §12
 * Path: ui/src/components/tenant-admin/OteHistoryDataReview.jsx
 *
 * S473: New screen — OTE history CSV bulk import. Used import-logs workaround
 *   for read path (GET /api/v1/positions/ote-history did not exist).
 * S474 OI-OTE-HISTORY-READ-ENDPOINT: real endpoint built.
 *   Switched to GET /api/v1/positions/ote-history.
 *   Filters: employee_id, effective_date_from, effective_date_to.
 *   No pagination required — OTE history is low-volume (2–4 records per employee per year).
 *   RBAC: comp_admin only.
 *
 * Write pattern: INSERT only. All-or-nothing. No 207.
 * Field contract: employee_id, ote_amount, ote_currency_code, effective_date, end_date
 * source_system: direct_api · file_type: ote_history
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { API_BASE } from './DataSetupFlowShell';

const OTE_HISTORY_REQUIRED_COLS = [
  'employee_id',
  'ote_amount',
  'ote_currency_code',
  'effective_date',
  'end_date',
];

function getErrorMessage(error, fallback) {
  return error instanceof Error && error.message ? error.message : fallback;
}

function OteHistoryDataReview({ tier = 'essentials', onBack, onNext }) {
  const { user } = useAuth();

  // Read state
  const [oteData, setOteData]               = useState([]);
  const [loading, setLoading]               = useState(false);
  const [inlineError, setInlineError]       = useState('');
  const [blockingError, setBlockingError]   = useState('');
  const [toast, setToast]                   = useState(null);

  // Filters
  const [filterEmployee, setFilterEmployee]     = useState('');
  const [filterDateFrom, setFilterDateFrom]     = useState('');
  const [filterDateTo, setFilterDateTo]         = useState('');

  // CSV state
  const [csvFile, setCsvFile]           = useState(null);
  const [csvDryResult, setCsvDryResult] = useState(null);
  const [csvCommitted, setCsvCommitted] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvError, setCsvError]         = useState('');

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => { setToast(null); }, 3000);
  }, []);

  const fetchJson = useCallback(async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: { ...(options.headers || {}) },
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `Request failed with status ${response.status}`);
    }
    if (response.status === 204) return null;
    return response.json();
  }, []);

  // S474: real endpoint GET /api/v1/positions/ote-history.
  // Low-volume entity — no pagination needed. All records returned in one call.
  const loadOteHistory = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setInlineError('');
    try {
      const params = new URLSearchParams();
      if (filterEmployee.trim()) params.set('employee_id', filterEmployee.trim());
      if (filterDateFrom.trim()) params.set('effective_date_from', filterDateFrom.trim());
      if (filterDateTo.trim())   params.set('effective_date_to', filterDateTo.trim());

      const qs = params.toString();
      const data = await fetchJson(`${API_BASE}/positions/ote-history${qs ? `?${qs}` : ''}`);
      setOteData(data?.data ?? []);
    } catch (error) {
      setInlineError(getErrorMessage(error, 'Unable to fetch OTE history.'));
    } finally {
      setLoading(false);
    }
  }, [fetchJson, user, filterEmployee, filterDateFrom, filterDateTo]);

  useEffect(() => {
    setOteData([]);
    loadOteHistory();
  }, [loadOteHistory]);

  const ensureOteHistoryColumnMapping = useCallback(async () => {
    const mappings = [
      { source_column_name: 'employee_id',      target_column_name: 'employee_id',      is_required: true },
      { source_column_name: 'ote_amount',        target_column_name: 'ote_amount',        is_required: true },
      { source_column_name: 'ote_currency_code', target_column_name: 'ote_currency_code', is_required: true },
      { source_column_name: 'effective_date',    target_column_name: 'effective_date',    is_required: true },
      { source_column_name: 'end_date',          target_column_name: 'end_date',          is_required: true },
    ];
    const res = await fetch(`${API_BASE}/import/column-mapping`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_system: 'direct_api', file_type: 'ote_history', mappings }),
    });
    if (!res.ok) throw new Error('Failed to seed OTE history column mapping.');
  }, []);

  const runCsvValidate = useCallback(async (file) => {
    if (!file) return;
    setCsvImporting(true);
    setCsvError('');
    setCsvDryResult(null);
    setCsvCommitted(false);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { setCsvError('CSV has no data rows.'); return; }
      const headers = lines[0].split(',').map((h) => h.trim());
      const missing = OTE_HISTORY_REQUIRED_COLS.filter((c) => !headers.includes(c));
      if (missing.length > 0) { setCsvError(`Missing required columns: ${missing.join(', ')}`); return; }
      setCsvDryResult({ rows: lines.length - 1, warnings: [], errors: [] });
    } catch { setCsvError('Failed to parse CSV.'); }
    finally { setCsvImporting(false); }
  }, []);

  const runCsvCommit = useCallback(async (file) => {
    if (!file || !csvDryResult) return;
    setCsvImporting(true);
    setCsvError('');
    try {
      await ensureOteHistoryColumnMapping();
      const fd = new FormData();
      fd.append('file', file);
      fd.append('source_system', 'direct_api');
      fd.append('file_type', 'ote_history');
      const res = await fetch(`${API_BASE}/import/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) {
        const detail = json?.error?.message || json?.message || json?.detail || `Error ${res.status}`;
        setCsvError(detail);
        return;
      }
      setCsvCommitted(true);
      setCsvDryResult(null);
      setCsvFile(null);
      showToast(`${csvDryResult.rows} OTE history record(s) loaded.`);
      // Reload from real endpoint after successful import
      setOteData([]);
      await loadOteHistory();
    } catch (e) { setCsvError(e.message || 'Commit failed.'); }
    finally { setCsvImporting(false); }
  }, [csvDryResult, ensureOteHistoryColumnMapping, loadOteHistory, showToast]);

  const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
  const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
  const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
  const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';

  if (!user) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">OTE History</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication is required to review OTE history.
        </div>
      </section>
    );
  }

  if (blockingError) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">OTE History</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {blockingError}
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-6">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-primary))] mb-1">SETUP-01b</p>
          <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">OTE History</h2>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Load on-target earnings history for each position. Required for time-weighted
            variable compensation when OTE changes during the measurement period.
          </p>
        </div>
      </header>

      {/* Toast */}
      {toast ? (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
          {toast}
        </div>
      ) : null}

      {/* Inline error */}
      {inlineError ? (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {inlineError}
        </div>
      ) : null}

      {/* CSV Bulk Import */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-5 shadow-sm">
        <div>
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">CSV Bulk Import</h3>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Upload <strong>OteHistory.csv</strong> to load OTE history records in one pass.
            All rows are validated before any are written — the entire batch succeeds or fails together.
          </p>
        </div>

        {csvError ? (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            {csvError}
          </div>
        ) : null}

        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[rgb(var(--color-primary))] text-tok-xs font-bold text-white">1</span>
            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              OteHistory.csv — OTE history rows
              {csvCommitted ? <span className="ml-2 text-[rgb(var(--color-success))] font-normal">✓ Loaded</span> : null}
            </span>
          </div>

          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Required columns: <code>employee_id, ote_amount, ote_currency_code, effective_date, end_date</code>
          </p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            <strong>Note:</strong> Date ranges per employee must not overlap existing records.
            Re-importing identical records (same dates, same amount) is safe — duplicates are skipped automatically.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept=".csv"
              className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setCsvFile(f);
                setCsvDryResult(null);
                setCsvCommitted(false);
                setCsvError('');
              }}
            />
            <button
              type="button"
              className={btnSecondary}
              disabled={!csvFile || csvImporting}
              onClick={() => runCsvValidate(csvFile)}
            >
              {csvImporting && !csvDryResult ? 'Validating…' : 'Validate'}
            </button>
          </div>

          {csvDryResult ? (
            <div className="mt-1 flex flex-col gap-2">
              <div className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                Rows parsed: <strong>{csvDryResult.rows}</strong> · Records to load: <strong>{csvDryResult.rows}</strong>
              </div>
              {csvDryResult.errors?.length > 0 ? (
                <p className="text-tok-sm text-[rgb(var(--color-danger))]">{csvDryResult.errors.length} error(s) — fix CSV and re-upload.</p>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-tok-sm text-[rgb(var(--color-success))]">
                    ✓ Validation passed — {csvDryResult.rows} record(s) ready to load.
                  </span>
                  <button
                    type="button"
                    className={btnPrimary}
                    disabled={csvImporting}
                    onClick={() => runCsvCommit(csvFile)}
                  >
                    {csvImporting ? 'Loading…' : 'Confirm and load'}
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* OTE history records — filters + table */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">OTE history records</h3>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-tok-xs text-[rgb(var(--color-text-muted))]">Employee ID</label>
            <input
              type="text"
              placeholder="e.g. EMP-001"
              value={filterEmployee}
              onChange={(e) => setFilterEmployee(e.target.value)}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] w-32"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-tok-xs text-[rgb(var(--color-text-muted))]">Effective from</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] w-36"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-tok-xs text-[rgb(var(--color-text-muted))]">Effective to</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] w-36"
            />
          </div>
          <button type="button" className={btnSecondary} onClick={() => loadOteHistory()} disabled={loading}>
            {loading ? 'Loading…' : 'Apply filters'}
          </button>
          <button
            type="button"
            className={btnSecondary}
            disabled={loading}
            onClick={() => { setFilterEmployee(''); setFilterDateFrom(''); setFilterDateTo(''); }}
          >
            Clear
          </button>
        </div>

        {loading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>}

        {oteData.length === 0 && !loading ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No OTE history records found. Upload a CSV above to begin.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-tok-sm border-collapse">
              <thead>
                <tr>
                  <th className={thClass}>Employee ID</th>
                  <th className={thClass}>Name</th>
                  <th className={thClass}>OTE amount</th>
                  <th className={thClass}>Currency</th>
                  <th className={thClass}>Effective date</th>
                  <th className={thClass}>End date</th>
                  <th className={thClass}>Source</th>
                </tr>
              </thead>
              <tbody>
                {oteData.map((row) => (
                  <tr key={row.ote_history_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                    <td className={tdClass}>{row.employee_id || '—'}</td>
                    <td className={tdClass}>{row.full_name || '—'}</td>
                    <td className={tdClass}>{row.ote_amount ?? '—'}</td>
                    <td className={tdClass}>{row.ote_currency_code || '—'}</td>
                    <td className={tdClass}>{row.effective_date ? new Date(row.effective_date).toLocaleDateString() : '—'}</td>
                    <td className={tdClass}>{row.end_date ? new Date(row.end_date).toLocaleDateString() : '—'}</td>
                    <td className={tdClass}>{row.source_system || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-tok-xs text-[rgb(var(--color-text-muted))]">{oteData.length} record(s)</p>
          </div>
        )}

        <div className="flex items-center gap-2">
          <button type="button" className={btnSecondary} onClick={() => loadOteHistory()} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {/* Footer nav */}
      <footer className="flex items-center gap-3 pt-4 border-t border-[rgb(var(--color-border))]">
        <button type="button" className={btnSecondary} onClick={onBack}>Back</button>
        <button type="button" className={btnPrimary} onClick={onNext}>Continue to quotas</button>
      </footer>
    </section>
  );
}

export default OteHistoryDataReview;
